<template>
    <div>
        <v-row justify="center">
            <v-col cols="12" lg="10" class="admin-container">

                <!-- ============ KPI SUMMARY CARDS (מוסתר ל-agent) ============ -->
                <v-row v-if="showStatistics" class="kpi-row">
                    <v-col cols="12" sm="4" class="kpi-col">
                        <v-card class="kpi-card kpi-current" elevation="3">
                            <div class="kpi-icon-wrap">
                                <v-icon size="32" color="white">mdi-phone-in-talk</v-icon>
                            </div>
                            <div class="kpi-content">
                                <div class="kpi-label">שיחות פעילות כעת</div>
                                <div class="kpi-value">{{ totalActiveCalls }}</div>
                                <div class="kpi-sub">
                                    <span class="kpi-sub-item">
                                        <v-icon x-small color="white" class="ml-1">mdi-phone-incoming</v-icon>
                                        {{ inboundActiveCalls }} נכנסות
                                    </span>
                                    <span class="kpi-sub-item">
                                        <v-icon x-small color="white" class="ml-1">mdi-phone-outgoing</v-icon>
                                        {{ outboundActiveCalls }} יוצאות
                                    </span>
                                </div>
                            </div>
                        </v-card>
                    </v-col>

                    <v-col cols="12" sm="4" class="kpi-col">
                        <v-card class="kpi-card kpi-peak" elevation="3">
                            <div class="kpi-icon-wrap">
                                <v-icon size="32" color="white">mdi-chart-line-variant</v-icon>
                            </div>
                            <div class="kpi-content">
                                <div class="kpi-label">שיא היום</div>
                                <div class="kpi-value">{{ todayPeak.total }}</div>
                                <div class="kpi-sub">
                                    <span class="kpi-sub-item">
                                        <v-icon x-small color="white" class="ml-1">mdi-phone-incoming</v-icon>
                                        {{ todayPeak.inbound }} נכנסות
                                    </span>
                                    <span class="kpi-sub-item">
                                        <v-icon x-small color="white" class="ml-1">mdi-phone-outgoing</v-icon>
                                        {{ todayPeak.outbound }} יוצאות
                                    </span>
                                </div>
                            </div>
                        </v-card>
                    </v-col>

                    <v-col cols="12" sm="4" class="kpi-col">
                        <v-card class="kpi-card kpi-avg" elevation="3">
                            <div class="kpi-icon-wrap">
                                <v-icon size="32" color="white">mdi-clock-time-eight-outline</v-icon>
                            </div>
                            <div class="kpi-content">
                                <div class="kpi-label">ממוצע שעתי היום</div>
                                <div class="kpi-value">{{ todayHourlyAvg }}</div>
                                <div class="kpi-sub">
                                    <span class="kpi-sub-item">
                                        מבוסס על {{ activeHoursCount }} שעות
                                    </span>
                                </div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- ============ TABS ============ -->
                <v-card class="tabs-card" elevation="2">
                    <v-tabs v-model="activeTab" color="#0d2c6d" background-color="white" grow show-arrows
                        slider-color="#0d2c6d" slider-size="3">
                        <v-tab class="tab-item">
                            <v-icon left>mdi-phone-in-talk</v-icon>
                            שיחות פעילות
                        </v-tab>
                        <v-tab v-if="showStatistics" class="tab-item">
                            <v-icon left>mdi-chart-bar</v-icon>
                            סטטיסטיקות יומיות
                        </v-tab>
                        <v-tab v-if="showStatistics" class="tab-item">
                            <v-icon left>mdi-calendar-month</v-icon>
                            סטטיסטיקות חודשיות
                        </v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="activeTab">

                        <!-- ============ TAB 1: ACTIVE CALLS ============ -->
                        <v-tab-item>
                            <div class="tab-content">
                                <v-card flat class="filter-section">
                                    <v-row align="center" no-gutters>
                                        <v-col cols="12" :md="isAdmin ? 4 : 5" class="px-2">
                                            <v-text-field v-model="search" prepend-inner-icon="mdi-magnify"
                                                label="חיפוש שיחה" outlined dense hide-details background-color="white"
                                                clearable @input="onSearchInput"></v-text-field>
                                        </v-col>

                                        <!-- ============ פילטר סוג ספק (admin בלבד) ============ -->
                                        <v-col v-if="isAdmin" cols="12" md="3" class="px-2 mt-2 mt-md-0">
                                            <v-select v-model="selectedProvider" :items="providerOptions"
                                                item-text="text" item-value="value" label="סוג ספק"
                                                prepend-inner-icon="mdi-server-network" outlined dense hide-details
                                                background-color="white" clearable @change="changeProvider"></v-select>
                                        </v-col>

                                        <v-col cols="12" :md="isAdmin ? 5 : 7"
                                            class="d-flex justify-md-end justify-center px-2 mt-2 mt-md-0">
                                            <v-chip-group v-model="selectedTypeIndex" mandatory
                                                active-class="primary--text" class="filter-chips">
                                                <v-chip v-for="filterOption in filterOptions" :key="filterOption.value"
                                                    :value="filterOption.value" class="filter-chip" outlined
                                                    @click="changeFilter(filterOption.value)">
                                                    <v-icon left small>{{ filterOption.icon }}</v-icon>
                                                    {{ filterOption.text }}
                                                </v-chip>
                                            </v-chip-group>
                                            <v-chip v-if="refreshing" color="primary" small class="ml-2 refresh-chip">
                                                <v-icon left small>mdi-refresh</v-icon>
                                                מרענן...
                                            </v-chip>
                                        </v-col>
                                    </v-row>
                                </v-card>

                                <v-data-table id="table1" :headers="headers" :items="calls"
                                    :server-items-length="totalItems" :page.sync="currentPage"
                                    :items-per-page.sync="itemsPerPage" :loading="progressShow" :sort-by.sync="sortBy"
                                    :sort-desc.sync="sortDesc" loading-text="טוען נתונים..." :footer-props="{
                                        'items-per-page-options': [20, 50, 100, 500, 1000],
                                        'items-per-page-text': 'שורות בעמוד:'
                                    }" class="calls-table" @update:page="onPageChange"
                                    @update:items-per-page="onItemsPerPageChange" @update:sort-by="onSortChange"
                                    @update:sort-desc="onSortChange">

                                    <template v-if="$vuetify.breakpoint.mdAndUp" v-for="header in headers"
                                        v-slot:[`header.${header.value}`]="{ header }">
                                        <span class="table-header-text">{{ header.text }}</span>
                                    </template>

                                    <template v-if="$vuetify.breakpoint.mdAndUp" #item="{ item, index }">
                                        <tr class="table-row">
                                            <td class="table-cell">{{ (currentPage - 1) * itemsPerPage + index + 1 }}
                                            </td>
                                            <td class="table-cell">
                                                <div class="caller-name">
                                                    <v-icon small color="#0d2c6d" class="ml-1">mdi-account</v-icon>
                                                    {{ item.callerName || 'לא ידוע' }}
                                                </div>
                                            </td>
                                            <td class="table-cell">
                                                <div class="caller-number">
                                                    <v-icon small color="#0d2c6d" class="ml-1">mdi-phone</v-icon>
                                                    {{ item.calleridnum }}
                                                </div>
                                            </td>
                                            <td class="table-cell">{{ item.accountcode }}</td>
                                            <td class="table-cell">{{ item.ivrNumber }}</td>
                                            <td class="table-cell">{{ formatContextName(item.context) }}</td>
                                            <td class="table-cell">
                                                <v-chip small :color="getCallTypeColor(item.type)" text-color="white"
                                                    class="call-type-chip">
                                                    {{ item.type === "inbound" ? "נכנסת" : "יוצאת" }}
                                                </v-chip>
                                            </td>
                                            <td class="table-cell">
                                                <div class="duration-display">
                                                    <v-icon small color="#0d2c6d"
                                                        class="ml-1">mdi-clock-outline</v-icon>
                                                    {{ item.duration }}
                                                </div>
                                            </td>
                                            <td class="table-cell">
                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{ on }">
                                                        <v-btn icon small color="error" v-on="on"
                                                            @click="hangupCall(item, index)" class="hangup-button">
                                                            <v-icon>mdi-phone-hangup</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>ניתוק שיחה</span>
                                                </v-tooltip>
                                            </td>
                                        </tr>
                                    </template>

                                    <template v-slot:item.actions="{ item, index }">
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on }">
                                                <v-btn icon small color="error" v-on="on"
                                                    @click="hangupCall(item, index)" class="hangup-button">
                                                    <v-icon>mdi-phone-hangup</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>ניתוק שיחה</span>
                                        </v-tooltip>
                                    </template>

                                    <template v-slot:no-data>
                                        <div class="empty-state">
                                            <v-icon size="64" color="#0d2c6d">mdi-phone-off</v-icon>
                                            <span class="empty-text">לא נמצאו שיחות פעילות</span>
                                        </div>
                                    </template>
                                </v-data-table>
                            </div>
                        </v-tab-item>

                        <!-- ============ TAB 2: DAILY STATISTICS (מוסתר ל-agent) ============ -->
                        <v-tab-item v-if="showStatistics">
                            <div class="tab-content">
                                <v-row align="center" class="mb-4">
                                    <v-col cols="12" sm="6" md="4">
                                        <v-menu v-model="dateMenu" :close-on-content-click="false"
                                            transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field v-model="selectedDate" label="בחר תאריך"
                                                    prepend-inner-icon="mdi-calendar" readonly outlined dense
                                                    hide-details v-bind="attrs" v-on="on"></v-text-field>
                                            </template>
                                            <v-date-picker v-model="selectedDate" :max="today" locale="he"
                                                @input="dateMenu = false; loadDailyStats()" no-title></v-date-picker>
                                        </v-menu>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="8" class="d-flex justify-md-end">
                                        <v-btn-toggle v-model="dailyMetric" mandatory color="#0d2c6d" dense>
                                            <v-btn value="inbound" small>
                                                <v-icon left small>mdi-phone-incoming</v-icon>
                                                נכנסות
                                            </v-btn>
                                            <v-btn value="outbound" small>
                                                <v-icon left small>mdi-phone-outgoing</v-icon>
                                                יוצאות
                                            </v-btn>
                                            <v-btn value="total" small>
                                                <v-icon left small>mdi-phone</v-icon>
                                                סה"כ
                                            </v-btn>
                                        </v-btn-toggle>
                                    </v-col>
                                </v-row>

                                <v-row v-if="hasDailyData" class="mini-stats-row mb-3" no-gutters>
                                    <v-col cols="4" class="mini-stat-col">
                                        <div class="mini-stat">
                                            <div class="mini-stat-label">שיא ביום</div>
                                            <div class="mini-stat-value">{{ dailyMetricPeak }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="4" class="mini-stat-col">
                                        <div class="mini-stat">
                                            <div class="mini-stat-label">ממוצע</div>
                                            <div class="mini-stat-value">{{ dailyMetricAvg }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="4" class="mini-stat-col">
                                        <div class="mini-stat">
                                            <div class="mini-stat-label">שעת שיא</div>
                                            <div class="mini-stat-value">{{ dailyPeakHour }}</div>
                                        </div>
                                    </v-col>
                                </v-row>

                                <v-card flat outlined class="chart-card pa-4">
                                    <div v-if="loadingStats" class="loading-overlay">
                                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                    </div>
                                    <div v-else-if="!hasDailyData" class="empty-state">
                                        <v-icon size="64" color="#0d2c6d">mdi-chart-line</v-icon>
                                        <span class="empty-text">אין נתונים לתאריך זה</span>
                                    </div>
                                    <div v-else>
                                        <div class="chart-title">
                                            שיא {{ metricLabel(dailyMetric) }} לפי שעות - {{
                                                formatDateDisplay(selectedDate) }}
                                        </div>
                                        <div class="chart-wrapper">
                                            <div class="y-axis">
                                                <div v-for="(label, idx) in yAxisLabels(dailyChartMax)" :key="idx"
                                                    class="y-label">{{ label }}
                                                </div>
                                            </div>
                                            <div class="chart-area">
                                                <div class="grid-lines">
                                                    <div v-for="n in 4" :key="n" class="grid-line"></div>
                                                </div>
                                                <div class="bars-container">
                                                    <div v-for="(hour, idx) in dailyChartData" :key="idx"
                                                        class="bar-wrap">
                                                        <div class="bar-tooltip"
                                                            :class="{ 'tooltip-visible': hour.value > 0 }">
                                                            <strong>{{ hour.label }}</strong><br>
                                                            {{ hour.value }} {{ metricLabel(dailyMetric) }}
                                                        </div>
                                                        <div class="bar-value"
                                                            :class="{ 'bar-value-visible': hour.value > 0 }">
                                                            {{ hour.value }}
                                                        </div>
                                                        <div class="bar daily-bar" :style="{
                                                            height: hour.heightPct + '%'
                                                        }">
                                                        </div>
                                                        <div class="bar-label">{{ hour.label }}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-card>
                            </div>
                        </v-tab-item>

                        <!-- ============ TAB 3: MONTHLY STATISTICS (מוסתר ל-agent) ============ -->
                        <v-tab-item v-if="showStatistics">
                            <div class="tab-content">
                                <v-row align="center" class="mb-4">
                                    <v-col cols="12" sm="6" md="4">
                                        <v-menu v-model="monthMenu" :close-on-content-click="false"
                                            transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field v-model="selectedMonth" label="בחר חודש"
                                                    prepend-inner-icon="mdi-calendar-month" readonly outlined dense
                                                    hide-details v-bind="attrs" v-on="on"></v-text-field>
                                            </template>
                                            <v-date-picker v-model="selectedMonth" :max="currentMonth" type="month"
                                                locale="he" @input="monthMenu = false; loadMonthlyStats()"
                                                no-title></v-date-picker>
                                        </v-menu>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="8" class="d-flex justify-md-end">
                                        <v-btn-toggle v-model="monthlyMetric" mandatory color="#0d2c6d" dense>
                                            <v-btn value="inbound" small>
                                                <v-icon left small>mdi-phone-incoming</v-icon>
                                                נכנסות
                                            </v-btn>
                                            <v-btn value="outbound" small>
                                                <v-icon left small>mdi-phone-outgoing</v-icon>
                                                יוצאות
                                            </v-btn>
                                            <v-btn value="total" small>
                                                <v-icon left small>mdi-phone</v-icon>
                                                סה"כ
                                            </v-btn>
                                        </v-btn-toggle>
                                    </v-col>
                                </v-row>

                                <v-row v-if="hasMonthlyData" class="mini-stats-row mb-3" no-gutters>
                                    <v-col cols="4" class="mini-stat-col">
                                        <div class="mini-stat">
                                            <div class="mini-stat-label">שיא בחודש</div>
                                            <div class="mini-stat-value">{{ monthlyMetricPeak }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="4" class="mini-stat-col">
                                        <div class="mini-stat">
                                            <div class="mini-stat-label">ממוצע יומי</div>
                                            <div class="mini-stat-value">{{ monthlyMetricAvg }}</div>
                                        </div>
                                    </v-col>
                                    <v-col cols="4" class="mini-stat-col">
                                        <div class="mini-stat">
                                            <div class="mini-stat-label">יום שיא</div>
                                            <div class="mini-stat-value">{{ monthlyPeakDay }}</div>
                                        </div>
                                    </v-col>
                                </v-row>

                                <v-card flat outlined class="chart-card pa-4">
                                    <div v-if="loadingStats" class="loading-overlay">
                                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                    </div>
                                    <div v-else-if="!hasMonthlyData" class="empty-state">
                                        <v-icon size="64" color="#0d2c6d">mdi-chart-bar</v-icon>
                                        <span class="empty-text">אין נתונים לחודש זה</span>
                                    </div>
                                    <div v-else>
                                        <div class="chart-title">
                                            שיא {{ metricLabel(monthlyMetric) }} יומי - {{
                                                formatMonthDisplay(selectedMonth) }}
                                        </div>
                                        <div class="chart-wrapper">
                                            <div class="y-axis">
                                                <div v-for="(label, idx) in yAxisLabels(monthlyChartMax)" :key="idx"
                                                    class="y-label">{{ label }}
                                                </div>
                                            </div>
                                            <div class="chart-area">
                                                <div class="grid-lines">
                                                    <div v-for="n in 4" :key="n" class="grid-line"></div>
                                                </div>
                                                <div class="bars-container">
                                                    <div v-for="(day, idx) in monthlyChartData" :key="idx"
                                                        class="bar-wrap">
                                                        <div class="bar-tooltip"
                                                            :class="{ 'tooltip-visible': day.value > 0 }">
                                                            <strong>{{ day.date }}</strong><br>
                                                            {{ day.value }} {{ metricLabel(monthlyMetric) }}
                                                        </div>
                                                        <div class="bar-value"
                                                            :class="{ 'bar-value-visible': day.value > 0 }">
                                                            {{ day.value }}
                                                        </div>
                                                        <div class="bar monthly-bar" :style="{
                                                            height: day.heightPct + '%'
                                                        }">
                                                        </div>
                                                        <div class="bar-label">{{ day.label }}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-card>
                            </div>
                        </v-tab-item>

                    </v-tabs-items>
                </v-card>
            </v-col>
        </v-row>
        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
    </div>
</template>

<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import Auth from '@/services/auth.service.js'

export default {
    components: {
        SnackBar,
    },
    data: () => ({
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",
        lineNumber: "",
        userRole: "",
        progressShow: false,
        refreshing: false,
        search: "",
        searchDebounceTimer: null,
        intervalId: null,
        statsIntervalId: null,
        activeTab: 0,

        // ===== Active calls (paginated) =====
        calls: [],
        totalItems: 0,
        summary: { total: 0, inbound: 0, outbound: 0 },
        currentPage: 1,
        itemsPerPage: 50,
        type: "all",
        selectedTypeIndex: 0,

        // ===== Provider filter (admin only) =====
        selectedProvider: null,
        providerOptions: [
            { text: 'moshe', value: 'moshe' },
            { text: 'moreX', value: 'moreX' },
            { text: '012', value: '012' },
            { text: 'hotMobile', value: 'hotMobile' },
            { text: 'telzar-019', value: 'telzar-019' },
        ],

        // ===== Statistics =====
        loadingStats: false,
        dateMenu: false,
        monthMenu: false,
        selectedDate: new Date().toISOString().substr(0, 10),
        selectedMonth: new Date().toISOString().substr(0, 7),
        dailyStats: null,
        monthlyStats: null,
        dailyMetric: "inbound",
        monthlyMetric: "inbound",
        sortBy: "",
        sortDesc: false,
        abortController: null,
        abortDailyStatsController: null,
        abortMonthlyStatsController: null,
        filterOptions: [
            { text: 'כל השיחות', value: 'all', icon: 'mdi-phone' },
            { text: 'נכנסות', value: 'inbound', icon: 'mdi-phone-incoming' },
            { text: 'יוצאות', value: 'outbound', icon: 'mdi-phone-outgoing' }
        ],
        headers: [
            { text: '#', value: "index", sortable: false, width: '50px' },
            { text: 'שם', value: 'callerName', align: 'center' },
            { text: 'מספר המתקשר', value: 'calleridnum', align: 'center' },
            { text: 'יעד התקשרות', value: 'accountcode', align: 'center' },
            { text: 'יעד ראשי', value: 'ivrNumber', align: 'center' },
            { text: 'שלוחה/מיקום האזנה', value: 'context', align: 'center' },
            { text: 'סוג שיחה', value: 'type', width: '120px', align: 'center' },
            { text: 'זמן שיחה', value: 'duration', width: '120px', align: 'center' },
            { text: '', value: 'actions', sortable: false, width: '80px', align: 'center' },
        ],
    }),
    computed: {
        today() {
            return new Date().toISOString().substr(0, 10);
        },
        currentMonth() {
            return new Date().toISOString().substr(0, 7);
        },
        // agent לא רואה סטטיסטיקות
        showStatistics() {
            return this.userRole !== "agent";
        },
        // פילטר סוג ספק זמין ל-admin בלבד
        isAdmin() {
            return this.userRole === "admin";
        },
        totalActiveCalls() {
            return this.summary.total;
        },
        inboundActiveCalls() {
            return this.summary.inbound;
        },
        outboundActiveCalls() {
            return this.summary.outbound;
        },
        todayPeak() {
            if (!this.dailyStats) return { inbound: 0, outbound: 0, total: 0 };
            return this.dailyStats.dailyPeak || { inbound: 0, outbound: 0, total: 0 };
        },
        activeHoursCount() {
            if (!this.dailyStats || !this.dailyStats.hours) return 0;
            return Object.values(this.dailyStats.hours).filter(h => (h.total || 0) > 0).length;
        },
        todayHourlyAvg() {
            if (!this.dailyStats || !this.dailyStats.hours) return 0;
            const values = Object.values(this.dailyStats.hours)
                .map(h => h.total || 0)
                .filter(v => v > 0);
            if (!values.length) return 0;
            const sum = values.reduce((a, b) => a + b, 0);
            return Math.round(sum / values.length);
        },
        hasDailyData() {
            if (!this.dailyStats || !this.dailyStats.hours) return false;
            return Object.values(this.dailyStats.hours).some(h => (h[this.dailyMetric] || 0) > 0);
        },
        hasMonthlyData() {
            if (!this.monthlyStats || !this.monthlyStats.days || !this.monthlyStats.days.length) return false;
            return this.monthlyStats.days.some(d => (d.dailyPeak && d.dailyPeak[this.monthlyMetric]) > 0);
        },
        dailyChartData() {
            if (!this.dailyStats) return [];
            const hours = this.dailyStats.hours || {};
            const data = [];
            let max = 0;
            for (let h = 0; h < 24; h++) {
                const key = String(h).padStart(2, '0');
                const hourData = hours[key] || { inbound: 0, outbound: 0, total: 0 };
                const value = hourData[this.dailyMetric] || 0;
                if (value > max) max = value;
                data.push({ label: `${key}:00`, value, raw: hourData });
            }
            return data.map(d => ({
                ...d,
                heightPct: max > 0 ? (d.value > 0 ? Math.max((d.value / max) * 100, 3) : 0) : 0
            }));
        },
        dailyChartMax() {
            if (!this.dailyChartData.length) return 0;
            return Math.max(...this.dailyChartData.map(d => d.value));
        },
        dailyMetricPeak() {
            return this.dailyChartMax;
        },
        dailyMetricAvg() {
            const values = this.dailyChartData.map(d => d.value).filter(v => v > 0);
            if (!values.length) return 0;
            return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
        },
        dailyPeakHour() {
            if (!this.dailyChartData.length || this.dailyChartMax === 0) return '-';
            const peak = this.dailyChartData.find(d => d.value === this.dailyChartMax);
            return peak ? peak.label : '-';
        },
        monthlyChartData() {
            if (!this.monthlyStats || !this.monthlyStats.days) return [];
            let max = 0;
            const data = this.monthlyStats.days.map(d => {
                const value = (d.dailyPeak && d.dailyPeak[this.monthlyMetric]) || 0;
                if (value > max) max = value;
                const day = d.date.split('-')[2];
                return { label: day, value, date: d.date };
            });
            return data.map(d => ({
                ...d,
                heightPct: max > 0 ? (d.value > 0 ? Math.max((d.value / max) * 100, 3) : 0) : 0
            }));
        },
        monthlyChartMax() {
            if (!this.monthlyChartData.length) return 0;
            return Math.max(...this.monthlyChartData.map(d => d.value));
        },
        monthlyMetricPeak() {
            return this.monthlyChartMax;
        },
        monthlyMetricAvg() {
            const values = this.monthlyChartData.map(d => d.value).filter(v => v > 0);
            if (!values.length) return 0;
            return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
        },
        monthlyPeakDay() {
            if (!this.monthlyChartData.length || this.monthlyChartMax === 0) return '-';
            const peak = this.monthlyChartData.find(d => d.value === this.monthlyChartMax);
            return peak ? this.formatDateDisplay(peak.date) : '-';
        }
    },
    methods: {
        metricLabel(metric) {
            return metric === 'inbound' ? 'שיחות נכנסות'
                : metric === 'outbound' ? 'שיחות יוצאות'
                    : 'שיחות';
        },
        onSortChange() {
            this.currentPage = 1;
            this.getOnlineCalls();
        },
        yAxisLabels(max) {
            if (!max) return ['0', '', '', '', ''];
            const step = max / 4;
            return [
                max,
                Math.round(step * 3),
                Math.round(step * 2),
                Math.round(step),
                0
            ];
        },
        changeFilter(newType) {
            this.type = newType;
            this.currentPage = 1;
            this.progressShow = true;
            this.getOnlineCalls();
        },
        // שינוי פילטר סוג ספק (רלוונטי ל-admin בלבד). null/ריק = כל הספקים
        changeProvider() {
            if (!this.isAdmin) return;
            this.currentPage = 1;
            this.progressShow = true;
            this.getOnlineCalls();
        },
        onSearchInput() {
            if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.currentPage = 1;
                this.getOnlineCalls();
            }, 400);
        },
        onPageChange() {
            this.getOnlineCalls();
        },
        onItemsPerPageChange() {
            this.currentPage = 1;
            this.getOnlineCalls();
        },
        formatContextName(context) {
            if (!context) return '';
            return context === "welcome" ? "כניסה"
                : context === "main" ? "שלוחה ראשית"
                    : context === "Ad" ? "פרסומת"
                        : context === "welcome-after-Ad" ? "כניסה אחרי פרסומת"
                            : context === "main-7-auth" ? "שלוחה-אימות כניסה-7"
                                : context.includes("main-") ? context.replace("main", "שלוחה")
                                    : context;
        },
        getCallTypeColor(type) {
            return type === "inbound" ? "#4caf50" : "#ff9800";
        },
        async getOnlineCalls() {
            try {
                // ביטול בקשה קודמת אם עדיין רצה
                if (this.abortController) this.abortController.abort();
                this.abortController = new AbortController();

                this.refreshing = true;
                const token = localStorage.getItem("token");
                const params = new URLSearchParams({
                    type: this.type,
                    page: this.currentPage,
                    limit: this.itemsPerPage,
                });
                if (this.search) params.append('search', this.search);

                // ⬅️ הוספה: פילטר סוג ספק - נשלח רק ל-admin וכשנבחר ערך (אחרת התנהגות זהה לקודם)
                if (this.isAdmin && this.selectedProvider) {
                    params.append('provider', this.selectedProvider);
                }

                // ⬅️ הוספה: שליחת פרמטרי המיון
                const sortByVal = Array.isArray(this.sortBy) ? this.sortBy[0] : this.sortBy;
                const sortDescVal = Array.isArray(this.sortDesc) ? this.sortDesc[0] : this.sortDesc;
                if (sortByVal) {
                    params.append('sortBy', sortByVal);
                    params.append('sortDesc', sortDescVal ? 'true' : 'false');
                }

                const api = `${process.env.VUE_APP_BASE_URL}/online_calls/get_online_calls?${params.toString()}`;
                const res = await fetch(api, {
                    ...ApiServices.requestOptions("GET", "", token),
                    signal: this.abortController.signal
                });
                const data = await res.json();

                this.progressShow = false;
                this.refreshing = false;

                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בקבלת נתונים", "red");
                } else if (res.status === 200) {
                    if (Array.isArray(data)) {
                        this.calls = data;
                        this.totalItems = data.length;
                        this.summary = {
                            total: data.length,
                            inbound: data.filter(c => c.type === "inbound").length,
                            outbound: data.filter(c => c.type === "outbound").length
                        };
                    } else {
                        this.calls = data.items || [];
                        this.totalItems = data.total || 0;
                        if (data.summary) this.summary = data.summary;
                    }
                }
            } catch (error) {
                if (error.name === 'AbortError') return;
                this.refreshing = false;
                this.progressShow = false;
                this.showSnackBar("Error get files: " + error, "red");
            }
        },
        async hangupCall(item, index) {
            try {
                this.progressShow = true;
                const token = localStorage.getItem("token");
                const channelJSON = JSON.stringify({ channel: item.channel });
                const api = `${process.env.VUE_APP_BASE_URL}/online_calls/hangup_call`;
                const res = await fetch(api, ApiServices.requestOptions("POST", channelJSON, token));
                this.progressShow = false;

                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בניתוק השיחה", "red");
                } else if (res.status === 200) {
                    const idx = this.calls.findIndex(call => call.channel === item.channel);
                    if (idx !== -1) this.calls.splice(idx, 1);
                    this.showSnackBar("השיחה נותקה בהצלחה", "green");
                }
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("Error hangup call: " + error, "red");
            }
        },
        async loadDailyStats() {
            try {
                // ביטול בקשה קודמת אם עדיין רצה
                if (this.abortDailyStatsController) this.abortDailyStatsController.abort();
                this.abortDailyStatsController = new AbortController();

                this.loadingStats = true;
                const token = localStorage.getItem("token");
                const api = `${process.env.VUE_APP_BASE_URL}/online_calls/statistics/daily?date=${this.selectedDate}`;
                const res = await fetch(api, {
                    ...ApiServices.requestOptions("GET", "", token),
                    signal: this.abortDailyStatsController.signal
                });
                const data = await res.json();
                this.loadingStats = false;

                if (res.status === 200) {
                    this.dailyStats = data;
                } else {
                    this.showSnackBar("שגיאה בטעינת סטטיסטיקות יומיות", "red");
                }
            } catch (error) {
                if (error.name === 'AbortError') return;
                this.loadingStats = false;
                this.showSnackBar("Error loading daily stats: " + error, "red");
            }
        },
        async loadMonthlyStats() {
            try {
                // ביטול בקשה קודמת אם עדיין רצה
                if (this.abortMonthlyStatsController) this.abortMonthlyStatsController.abort();
                this.abortMonthlyStatsController = new AbortController();

                this.loadingStats = true;
                const token = localStorage.getItem("token");
                const api = `${process.env.VUE_APP_BASE_URL}/online_calls/statistics/monthly?yearMonth=${this.selectedMonth}`;
                const res = await fetch(api, {
                    ...ApiServices.requestOptions("GET", "", token),
                    signal: this.abortMonthlyStatsController.signal
                });
                const data = await res.json();
                this.loadingStats = false;

                if (res.status === 200) {
                    this.monthlyStats = data;
                } else {
                    this.showSnackBar("שגיאה בטעינת סטטיסטיקות חודשיות", "red");
                }
            } catch (error) {
                if (error.name === 'AbortError') return;
                this.loadingStats = false;
                this.showSnackBar("Error loading monthly stats: " + error, "red");
            }
        },
        formatDateDisplay(dateStr) {
            if (!dateStr) return '';
            const [y, m, d] = dateStr.split('-');
            return `${d}/${m}/${y}`;
        },
        formatMonthDisplay(monthStr) {
            if (!monthStr) return '';
            const [y, m] = monthStr.split('-');
            const months = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
                'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
            return `${months[parseInt(m) - 1]} ${y}`;
        },
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true;
        },
    },
    watch: {
        activeTab(newTab) {
            if (!this.showStatistics) return;
            // עם החבאת הטאבים עבור agent, האינדקסים נשארים 1 ו-2
            if (newTab === 1 && !this.dailyStats) this.loadDailyStats();
            if (newTab === 2 && !this.monthlyStats) this.loadMonthlyStats();
        }
    },
    mounted() {
        this.lineNumber = Auth.getLineNumber();
        // קריאת ה-role - יש לוודא שהפונקציה הזו קיימת ב-Auth service.
        // אם לא, להחליף ל: localStorage.getItem('role') או למקור הנכון אצלך
        this.userRole = Auth.getUserRole ? Auth.getUserRole() : (localStorage.getItem('role') || '');
        console.log(this.userRole);
        console.log(Auth.getUserRole());

        this.progressShow = true;
        this.getOnlineCalls();

        this.intervalId = setInterval(() => {
            if (this.activeTab === 0) {
                this.getOnlineCalls();
            }
        }, 5000);

        // טעינת סטטיסטיקות רק לתפקידים שמורשים
        if (this.showStatistics) {
            this.loadDailyStats();

            this.statsIntervalId = setInterval(() => {
                if (this.selectedDate === this.today) {
                    this.loadDailyStats();
                }
            }, 60000);
        }
    },
    beforeDestroy() {
        if (this.intervalId) clearInterval(this.intervalId);
        if (this.statsIntervalId) clearInterval(this.statsIntervalId);
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
        if (this.abortController) this.abortController.abort(); // ⬅️ תוספת
        if (this.abortMonthlyStatsController) this.abortMonthlyStatsController.abort(); // ⬅️ תוספת
        if (this.abortDailyStatsController) this.abortDailyStatsController.abort(); // ⬅️ תוספת

    },
}
</script>

<style scoped>
.admin-container {
    font-family: 'Heebo', sans-serif;
}

/* ===== KPI Cards ===== */
.kpi-row {
    margin: 0 -12px 24px -12px;
}

.kpi-col {
    padding: 0 12px;
}

.kpi-card {
    border-radius: 14px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    transition: all 0.3s ease;
    color: white;
    min-height: 110px;
}

.kpi-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

.kpi-current {
    background: linear-gradient(135deg, #0d2c6d 0%, #2952b3 100%);
}

.kpi-peak {
    background: linear-gradient(135deg, #1e8e3e 0%, #4caf50 100%);
}

.kpi-avg {
    background: linear-gradient(135deg, #e65100 0%, #ff9800 100%);
}

.kpi-icon-wrap {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.kpi-content {
    flex: 1;
    min-width: 0;
}

.kpi-label {
    font-size: 13px;
    opacity: 0.9;
    margin-bottom: 4px;
    font-weight: 500;
}

.kpi-value {
    font-size: 32px;
    font-weight: 700;
    line-height: 1.1;
    margin-bottom: 6px;
}

.kpi-sub {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    font-size: 12px;
    opacity: 0.95;
}

.kpi-sub-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: rgba(255, 255, 255, 0.18);
    padding: 3px 9px;
    border-radius: 10px;
    color: white;
    backdrop-filter: blur(4px);
}

/* ===== Tabs ===== */
.tabs-card {
    border-radius: 14px;
    overflow: hidden;
    margin-bottom: 40px;
}

.tab-item {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
}

.tab-content {
    padding: 20px;
    background: #fafbfc;
    min-height: 400px;
}

.filter-section {
    background: transparent !important;
    margin-bottom: 16px;
}

.filter-chips {
    gap: 8px;
}

.filter-chip {
    font-weight: 600;
    font-size: 14px;
    height: 36px;
    padding: 0 14px;
    border: 2px solid #e0e0e0;
    transition: all 0.3s ease;
}

.filter-chip:hover,
.filter-chip.v-chip--active {
    border-color: #0d2c6d;
    background-color: rgba(13, 44, 109, 0.05);
}

.refresh-chip {
    animation: pulse 1.5s infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 0.6;
    }

    50% {
        opacity: 1;
    }
}

/* ===== Data Table ===== */
.calls-table {
    background: white;
    border-radius: 8px;
}

.table-header-text {
    font-size: 16px;
    font-weight: 600;
    color: #0d2c6d;
}

.table-row {
    height: 60px !important;
    transition: background-color 0.2s ease;
}

.table-row:hover {
    background-color: rgba(13, 44, 109, 0.05) !important;
}

.table-cell {
    font-size: 14px;
    color: #333;
    text-align: center !important;
}

.caller-name,
.caller-number,
.duration-display {
    display: flex;
    align-items: center;
    justify-content: center;
}

.call-type-chip {
    font-weight: 600;
}

.hangup-button {
    transition: all 0.2s ease;
}

.hangup-button:hover {
    transform: scale(1.1);
}

/* ===== Mini stats summary above chart ===== */
.mini-stats-row {
    background: white;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    overflow: hidden;
}

.mini-stat-col {
    border-left: 1px solid #e0e0e0;
}

.mini-stat-col:last-child {
    border-left: none;
}

.mini-stat {
    padding: 14px 12px;
    text-align: center;
}

.mini-stat-label {
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
    font-weight: 500;
}

.mini-stat-value {
    font-size: 22px;
    font-weight: 700;
    color: #0d2c6d;
}

/* ===== Charts ===== */
.chart-card {
    border-radius: 12px;
    background: white;
    min-height: 380px;
    position: relative;
}

.chart-title {
    font-size: 17px;
    font-weight: 600;
    color: #0d2c6d;
    margin-bottom: 20px;
    text-align: center;
}

.chart-wrapper {
    display: flex;
    gap: 8px;
    height: 320px;
}

.y-axis {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 24px 0 24px 0;
    min-width: 40px;
    text-align: left;
    direction: ltr;
}

.y-label {
    font-size: 11px;
    color: #999;
    font-weight: 500;
    line-height: 1;
}

.chart-area {
    flex: 1;
    position: relative;
    padding-top: 24px;
    padding-bottom: 24px;
}

.grid-lines {
    position: absolute;
    inset: 24px 0 24px 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    pointer-events: none;
}

.grid-line {
    height: 1px;
    background: #f0f0f0;
}

.bars-container {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 6px;
    height: 100%;
    padding: 0 4px;
    border-bottom: 2px solid #d0d0d0;
    position: relative;
    z-index: 1;
    direction: rtl;
}

.bar-wrap {
    flex: 1 1 0;
    max-width: 60px;
    min-width: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    position: relative;
    cursor: pointer;
}

.bar-value {
    font-size: 11px;
    font-weight: 700;
    color: #0d2c6d;
    opacity: 0;
    transition: opacity 0.2s ease;
    white-space: nowrap;
    position: absolute;
    top: -22px;
    left: 50%;
    transform: translateX(-50%);
}

.bar-value-visible {
    opacity: 1;
}

.bar-tooltip {
    position: absolute;
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%);
    background: #0d2c6d;
    color: white;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 12px;
    line-height: 1.5;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    text-align: center;
}

.bar-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #0d2c6d;
}

.bar-wrap:hover .bar-tooltip.tooltip-visible {
    opacity: 1;
}

.bar-wrap:hover .bar-value {
    opacity: 1;
    transform: translateX(-50%) scale(1.15);
}

.bar {
    width: 100%;
    border-radius: 8px 8px 0 0;
    margin-top: auto;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.08);
    min-height: 0;
    position: relative;
}

.daily-bar {
    background: linear-gradient(180deg, #2952b3 0%, #0d2c6d 100%);
}

.monthly-bar {
    background: linear-gradient(180deg, #3f6dd0 0%, #0d2c6d 100%);
}

.bar-wrap:hover .bar {
    filter: brightness(1.15);
    transform: translateY(-2px);
    box-shadow: 0 -4px 12px rgba(13, 44, 109, 0.3);
}

.bar-label {
    font-size: 10px;
    color: #666;
    margin-top: 6px;
    font-weight: 500;
    white-space: nowrap;
    position: absolute;
    top: calc(100% + 4px);
}

.loading-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.7);
    z-index: 10;
}

/* ===== Empty State ===== */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
}

.empty-text {
    font-size: 18px;
    font-weight: 600;
    color: #0d2c6d;
    margin-top: 14px;
}

/* ===== Responsive ===== */
@media (max-width: 960px) {
    .kpi-card {
        padding: 16px;
        min-height: 90px;
    }

    .kpi-value {
        font-size: 26px;
    }

    .kpi-icon-wrap {
        width: 48px;
        height: 48px;
    }

    .table-header-text {
        font-size: 14px;
    }

    .table-cell {
        font-size: 13px;
    }

    .chart-wrapper {
        height: 260px;
    }
}

@media (max-width: 600px) {
    .tab-content {
        padding: 12px;
    }

    .chart-wrapper {
        height: 220px;
    }

    .bar-label {
        font-size: 9px;
    }

    .mini-stat-value {
        font-size: 18px;
    }
}
</style>